/**
 * Currency formatting utilities
 */

// Format currency with proper USD formatting
export function formatCurrency(amount) {
  // Handle null, undefined, and empty string cases
  if (amount == null || amount === '') {
    amount = 0;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}

// Format currency without decimal places for cleaner display
export function formatCurrencyClean(amount) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
}

// Format currency for input fields (without $ symbol, but with commas)
export function formatCurrencyInput(amount) {
  if (isNaN(amount) || amount === '') return '';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}

/**
 * Safely evaluate math expressions
 * Supports basic operations: +, -, *, /, (, )
 * Returns null if invalid expression
 */
function evaluateMathExpression(expression) {
  if (!expression || typeof expression !== 'string') return null;
  
  // Remove spaces and validate characters (only allow numbers, operators, parentheses, decimal points)
  const cleanExpression = expression.replace(/\s/g, '');
  if (!/^[0-9+\-*/().]+$/.test(cleanExpression)) return null;
  
  // Prevent division by zero and other invalid patterns
  if (/\/0(?!\.)/.test(cleanExpression)) return null;
  if (/[+\-*/]{2,}/.test(cleanExpression)) return null; // No consecutive operators
  if (/^[+\-*/]/.test(cleanExpression) && !/^[-]/.test(cleanExpression)) return null; // No leading operators except minus
  if (/[+\-*/]$/.test(cleanExpression)) return null; // No trailing operators
  
  try {
    // Use Function constructor for safe evaluation (better than eval)
    const result = Function(`"use strict"; return (${cleanExpression})`)();
    
    // Validate result is a finite number
    if (typeof result !== 'number' || !isFinite(result)) return null;
    
    return Math.round(result * 100) / 100; // Round to 2 decimal places
  } catch (error) {
    return null;
  }
}

// Parse currency input back to number
export function parseCurrencyInput(value) {
  if (!value || value === '') return 0;
  
  // First try to evaluate as math expression
  const mathResult = evaluateMathExpression(value.toString());
  if (mathResult !== null) {
    return mathResult;
  }
  
  // Fall back to regular parsing
  // Remove dollar signs, commas, and convert to number
  const cleanValue = value.toString().replace(/[\$,]/g, '');
  const parsed = parseFloat(cleanValue);
  return isNaN(parsed) ? 0 : parsed;
}

// Setup currency formatting for input fields
export function setupCurrencyInput(input, onCalculate) {
  if (!input) return;
  
  // Always use text type to support math expressions
  input.type = 'text';
  input.setAttribute('inputmode', 'text'); // Show full keyboard to support math operators on mobile
  input.setAttribute('title', 'Supports math expressions (e.g., 10+7, 25*4, 100/2)');
  
  // Add visual feedback classes
  function updateMathFeedback() {
    const inputValue = input.value.trim();
    
    // Remove all math-related classes first
    input.classList.remove('math-expression', 'math-valid');
    
    if (!inputValue) return;
    
    // Check if it looks like a math expression (contains operators)
    const hasMathOperators = /[+\-*/()]/.test(inputValue);
    
    if (hasMathOperators) {
      input.classList.add('math-expression');
      
      // Check if the expression is valid
      const mathResult = evaluateMathExpression(inputValue);
      if (mathResult !== null) {
        input.classList.add('math-valid');
      }
    }
  }
  
  // Format on blur (when user finishes typing)
  input.addEventListener('blur', function() {
    const value = parseCurrencyInput(this.value);
    if (value > 0) {
      this.value = formatCurrencyInput(value);
    }
    // Remove math feedback classes when formatted
    this.classList.remove('math-expression', 'math-valid');
  });
  
  // Remove formatting on focus (so user can edit easily)
  input.addEventListener('focus', function() {
    const value = parseCurrencyInput(this.value);
    if (value > 0) {
      this.value = value.toString();
    } else if (value === 0) {
      // Clear the field if it's exactly 0.00 to make editing easier
      this.value = '';
    }
  });
  
  // Handle input events for visual feedback and calculations
  input.addEventListener('input', function() {
    updateMathFeedback();
    
    // Trigger calculations with the parsed value
    if (onCalculate && typeof onCalculate === 'function') {
      onCalculate();
    }
  });
}